import React, {useContext, useEffect, useState} from 'react';
import UserForm from '../../views/user/user-form';
import UserTable from '../../views/user/user-table';
import PermissionTabs from '../../views/permission';
import {useIntl} from 'react-intl';
import useAlerts from '../../hooks/use-alerts';
import {ModalContext} from '../../apiContexts';
import useStore from '../../hooks/use-store';
import useServices from '../../hooks/use-services';
import {getSnapshot} from 'mobx-state-tree';
import useCustomers from '../../hooks/use-customers';
import useUsers from '../../hooks/use-users';
import useTimezone from '../../hooks/use-timezone';
import {formatDateTimeColumn} from '../../helpers/excel-helper';
import {observer} from 'mobx-react';
import {grantIntegrations} from '../../autobahn/features/integration/access';

const UserContainer = (props) => {
    const {store} = useStore();
    const {allTimezonesStore} = store;
    const {userService} = useServices();
    const {customers: customerStore} = useCustomers();
    const {auth, coordinateFormats} = store;
    const intl = useIntl();
    const {toast} = useAlerts();
    const {showModalLoading, isModalLoading} = useContext(ModalContext);
    const [view, setView] = useState('userList');
    const {users: userStore} = useUsers();
    const {timezone} = useTimezone();
    const [coordFormats, setCoordinateFormats] = useState([]);
    const [selectedUserId, setSelectedUserId] = useState(null);
    const [selectedUser, setSelectedUser] = useState(null);

    const users = userStore.list.map((user) => {
        const _user = {...user};
        const customer = customerStore.findById(_user.customerId);
        if (customer) {
            _user.customerName = customer.name;
        }
        const creator = userStore.getUserById(_user.createdByUserId);
        _user.creatorName = creator ? creator.name : '';
        _user.creatorEmail = creator ? creator.email : '';
        _user.createdAtFormatted = formatDateTimeColumn(_user.createdAt, timezone);
        _user.updatedAtFormatted = formatDateTimeColumn(_user.updatedAt, timezone);
        return _user;
    });

    useEffect(() => {
        (async () => {
            const items = [];
            coordinateFormats.formats.map((type) => {
                items.push({id: type.key, title: type.name});
            });
            setCoordinateFormats(items);
        })();
    }, []);

    const userForm = async (data) => {
        const userId = data?.id || null;
        setSelectedUserId(userId);
        setView('userForm');
    };

    const newRegister = async (data, integrationIds = []) => {
        try {
            showModalLoading(true);
            const result = await userService.save(data);

            // O vínculo de integração só existe depois do id, então ele sai daqui, não do formulário.
            if (integrationIds.length) {
                await grantIntegrations('user', result.id, integrationIds);
            }

            userStore.setUsers([...users, result]);
            setView('userList');
            toast.success(intl.formatMessage({id: 'sharedSavedSuccess'}));
        } catch (e) {
            toast.errorStatus(e);
        } finally {
            showModalLoading(false);
        }
    };

    const updateRegister = async (data, afterTo = 'userList') => {
        try {
            showModalLoading(true);
            const index = users.findIndex(({id}) => id === data.id);
            const result = await userService.update({...users[index], ...data});
            const newUsers = [...getSnapshot(userStore.users)];
            newUsers.splice(index, 1, result);
            userStore.setUsers(newUsers);
            setView(afterTo);
            toast.success(intl.formatMessage({id: 'sharedUpdatedSuccess'}));
        } catch (e) {
            toast.errorStatus(e);
        } finally {
            showModalLoading(false);
        }
    };

    const removeRegister = async (data) => {
        try {
            showModalLoading(true);
            await userService.remove(data.id);
            userStore.setUsers(users.filter(({id}) => id !== data.id));
            setView('userList');
            toast.success(intl.formatMessage({id: 'sharedRemovedSuccess'}));
        } catch (e) {
            toast.errorStatus(e);
        } finally {
            showModalLoading(false);
        }
    };

    // Mesma régua do backend: liberar integração é coisa de conta superior, e nunca sobre si mesma.
    const loggedUser = auth.getUser();
    const isSuperiorAccount = !!loggedUser &&
        (loggedUser.administrator || loggedUser.userLimit !== 0);
    const canGrantIntegrations = isSuperiorAccount && loggedUser.id !== selectedUserId;
    // Central normalmente cria usuario sem escolher Customer -- quem cria ja e o vinculo basico.
    // UserResource.add ja concede ManagedUser pra central de quem cria, e mais uma segunda
    // concessao pro cliente escolhido quando ele difere da central; a visibilidade de dispositivo
    // do usuario vem do customerId dele (appendCustomerHierarchy). O campo Customer serve pra
    // ADICIONAR um cliente por cima (estreita a visibilidade a ele), nao pra decidir se ha
    // vinculo. Sem este default, deixar o campo em branco (nao e obrigatorio) salvava o usuario
    // sem customerId nenhum -- nem o vinculo basico com a central, nem integracao, nem visibilidade.
    const defaultCustomerId = isSuperiorAccount ? loggedUser.customerId : undefined;

    const showPermissionTabs = (data) => {
        setSelectedUser({userId: data.id});
        setView('userPermissions');
    };

    return (
        <>
            {
                {
                    userList: (
                        <UserTable
                            list={users}
                            userForm={userForm}
                            removeRegister={removeRegister}
                            permissions={showPermissionTabs}
                            loading={isModalLoading}
                            intl={intl}
                        />
                    ),
                    userForm: (
                        <UserForm
                            cancel={() => setView('userList')}
                            data={!!selectedUserId ? userStore.getUserById(selectedUserId) : {}}
                            profile={auth.profile}
                            userStore={userStore}
                            coordinateFormats={coordFormats}
                            edit={!!selectedUserId}
                            newRegister={newRegister}
                            updateRegister={updateRegister}
                            customerStore={customerStore}
                            loading={isModalLoading}
                            timezoneStore={allTimezonesStore}
                            canGrantIntegrations={canGrantIntegrations}
                            defaultCustomerId={defaultCustomerId}
                        />
                    ),
                    userPermissions: (
                        <PermissionTabs
                            options={[
                                'device',
                                'notification',
                                'geofence',
                                'group',
                                'calendar',
                                'driver',
                                'maintenance']}
                            data={selectedUser}
                            cancel={() => setView('userList')}/>
                    ),
                }[view]
            }
        </>
    );
};


export default observer(UserContainer);
